<script lang="ts">
	/**
	 * The editor for one email template (Design §11, §13).
	 *
	 * Templates ship as **code defaults** and the table holds overrides only, so a template can
	 * never be missing — only customised. Three consequences shape this component:
	 *
	 * - **The badge is load-bearing.** "Customised" and "shipped default" are different states with
	 *   different consequences: a customised template stops receiving corrections to the shipped
	 *   copy, including a legally required one. It is stated in words, not implied by a colour.
	 * - **Preview never saves.** It renders the draft in the editor against sample data. Previewing
	 *   the *saved* row would mean saving a dunning email to see it, and a saved dunning email is
	 *   one customers receive.
	 * - **Reset to default deletes the override**, rather than copying today's default into it, so
	 *   the template rejoins the shipped copy for good. That is destructive to the wording the
	 *   operator typed, which is why it is a two-step confirmation.
	 *
	 * The expiry class is read-only on purpose. It is a property of *what the message is* — a
	 * receipt is a legal record and never expires; "we'll email you the moment it completes" is
	 * false the next morning — not of how the message is worded, so editing the copy must not
	 * silently change how long a queued copy of it stays truthful.
	 */

	import { Badge, Button, Input } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import type { EmailTemplatePreview, EmailTemplateSummary } from '../AppTypes';
	import { previewEmailTemplate, revertEmailTemplate, updateEmailTemplate } from '../AppNetwork';
	import { toastError, toastSuccess } from '../stores/toast';
	import ConfirmButton from './ConfirmButton.svelte';
	import EmptyState from './EmptyState.svelte';
	import FormField from './FormField.svelte';
	import Panel from './Panel.svelte';
	import RelativeDate from './RelativeDate.svelte';

	/** The template being edited, as returned by `listEmailTemplates()` / `getEmailTemplate()`. */
	export let template: EmailTemplateSummary;

	const dispatch = createEventDispatcher<{
		/** The row as the server now holds it, after a save or a reset. */
		changed: { template: EmailTemplateSummary };
		close: Record<string, never>;
	}>();

	let subject: string = '';
	let bodyHTML: string = '';
	let bodyText: string = '';

	let saving: boolean = false;
	let previewing: boolean = false;
	let reverting: boolean = false;
	let previewError: string = '';
	let preview: EmailTemplatePreview | null = null;

	/** Which editor a variable click inserts into — the one the operator was last typing in. */
	let lastFocused: 'subject' | 'bodyHTML' | 'bodyText' = 'bodyHTML';

	/**
	 * Looked up by id rather than kept in a component binding: the subject is a Flowbite
	 * `Input`, and binding that yields the *component* instance, whose `value` throws unless the
	 * component was compiled with accessors. The id is already there for the label association.
	 */
	const CONTROL_IDS: Record<'subject' | 'bodyHTML' | 'bodyText', string> = {
		subject: 'template-subject',
		bodyHTML: 'template-body-html',
		bodyText: 'template-body-text'
	};

	/** Reloads the draft whenever the parent points the editor at a different template. */
	let loadedKey: string = '';
	$: if (template && `${template.key}/${template.locale}` !== loadedKey) {
		loadedKey = `${template.key}/${template.locale}`;
		subject = template.subject;
		bodyHTML = template.bodyHTML;
		bodyText = template.bodyText;
		preview = null;
		previewError = '';
	}

	$: isDirty =
		subject !== template.subject ||
		bodyHTML !== template.bodyHTML ||
		bodyText !== template.bodyText;
	$: canSave = !saving && subject.trim().length > 0 && bodyText.trim().length > 0;

	/** The expiry class in words. Never a raw millisecond count — nobody reads 86400000. */
	function expiryCopy(milliseconds: number | null): string {
		if (milliseconds === null) {
			return 'This message never expires: it is sent whenever a sender becomes available, however long that takes.';
		}
		const hours = Math.round(milliseconds / (60 * 60 * 1000));
		// Hours up to two days, then days: "24 hours" is how the rule is stated, and "3 days" is
		// how a week-long dunning window is read. Never the millisecond count — nobody reads 86400000.
		const days = Math.round(hours / 24);
		const window =
			hours < 48
				? `${hours} ${hours === 1 ? 'hour' : 'hours'}`
				: `${days} ${days === 1 ? 'day' : 'days'}`;
		return `This message is discarded if it cannot be sent within ${window}: after that its wording is no longer true, and sending it late would mislead the customer.`;
	}

	$: expiryText = expiryCopy(template.expiresAfterMs);

	function fnInsertVariable(variable: string): void {
		const token = `{{${variable}}}`;
		const element = document.getElementById(CONTROL_IDS[lastFocused]) as
			| HTMLInputElement
			| HTMLTextAreaElement
			| null;

		if (element === null) {
			return;
		}

		const start = element.selectionStart ?? element.value.length;
		const end = element.selectionEnd ?? start;
		const next = `${element.value.slice(0, start)}${token}${element.value.slice(end)}`;

		if (lastFocused === 'subject') {
			subject = next;
		} else if (lastFocused === 'bodyHTML') {
			bodyHTML = next;
		} else {
			bodyText = next;
		}

		element.focus();
	}

	async function fnPreview(): Promise<void> {
		previewing = true;
		previewError = '';
		try {
			// The draft, never the saved row: that is the whole point of the button.
			preview = await previewEmailTemplate(template.key, template.locale, {
				subject,
				bodyHTML,
				bodyText
			});
		} catch (error) {
			preview = null;
			previewError = (error as Error).message;
		} finally {
			previewing = false;
		}
	}

	async function fnSave(): Promise<void> {
		if (!canSave) {
			return;
		}
		saving = true;
		try {
			const saved = await updateEmailTemplate(template.key, template.locale, {
				subject,
				bodyHTML,
				bodyText
			});
			template = saved;
			loadedKey = `${saved.key}/${saved.locale}`;
			subject = saved.subject;
			bodyHTML = saved.bodyHTML;
			bodyText = saved.bodyText;
			dispatch('changed', { template: saved });
			toastSuccess(`“${saved.key}” saved.`, 'It now overrides the shipped default.');
		} catch (error) {
			toastError('The template was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	async function fnRevert(): Promise<void> {
		reverting = true;
		try {
			const reverted = await revertEmailTemplate(template.key, template.locale);
			template = reverted;
			loadedKey = `${reverted.key}/${reverted.locale}`;
			subject = reverted.subject;
			bodyHTML = reverted.bodyHTML;
			bodyText = reverted.bodyText;
			preview = null;
			dispatch('changed', { template: reverted });
			toastSuccess(
				`“${reverted.key}” is back to the shipped default.`,
				'It will follow future corrections to that copy again.'
			);
		} catch (error) {
			toastError('The template was not reset.', (error as Error).message);
		} finally {
			reverting = false;
		}
	}
</script>

<Panel faIcon="pen-to-square" title={template.key}>
	<svelte:fragment slot="titleRight">
		<!-- Which of the two states this template is in, in words. -->
		{#if template.isOverridden}
			<Badge color="purple">Customised</Badge>
		{:else}
			<Badge color="dark">Shipped default</Badge>
		{/if}
		<Badge color="blue">{template.locale}</Badge>
	</svelte:fragment>

	<div class="space-y-4">
		<div class="widget bg-depth text-sm">
			<p class="card-label mb-1">
				<i class="fa-solid fa-hourglass-half text-[11px] mr-1" />Expiry class
			</p>
			<p class="text-default">{expiryText}</p>
			<p class="text-default mt-2 text-xs">
				This is fixed by what the message <em>is</em>, not by how it is worded, so it cannot be
				edited here: rewriting a receipt does not make it stop being a legal record.
			</p>
		</div>

		<div class="text-xs text-default">
			{#if template.isOverridden}
				This template is customised for this store. It no longer follows corrections to the shipped
				copy. Reset it to rejoin them.
				{#if template.updatedAt}
					Last edited <RelativeDate date={template.updatedAt} size="xs" />.
				{/if}
			{:else}
				This template is the copy this build ships. Saving a change here creates an override for
				this store.
			{/if}
		</div>

		{#if template.variables.length > 0}
			<div>
				<p class="card-label mb-2">
					<i class="fa-solid fa-code text-[11px] mr-1" />Available variables
				</p>
				<div class="flex flex-wrap gap-2">
					{#each template.variables as variable (variable)}
						<button
							type="button"
							class="widget bg-depth depth-hover px-2 py-1 text-xs font-mono"
							title={`Insert {{${variable}}}`}
							on:click={() => fnInsertVariable(variable)}
						>
							&#123;&#123;{variable}&#125;&#125;
						</button>
					{/each}
				</div>
				<p class="text-xs text-default mt-2">
					Anything else in double braces is left as written: the sender does not invent values.
				</p>
			</div>
		{/if}

		<form class="space-y-4" on:submit|preventDefault={fnSave}>
			<FormField id="template-subject" label="Subject" required>
				<Input
					id="template-subject"
					bind:value={subject}
					disabled={saving}
					on:focus={() => (lastFocused = 'subject')}
				/>
			</FormField>

			<FormField
				id="template-body-html"
				label="HTML body"
				description="What most mail clients show. Plain HTML. Nothing is compiled."
			>
				<textarea
					id="template-body-html"
					class="block w-full p-2.5 font-mono text-xs rounded-lg bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500"
					rows="12"
					bind:value={bodyHTML}
					disabled={saving}
					on:focus={() => (lastFocused = 'bodyHTML')}
				/>
			</FormField>

			<FormField
				id="template-body-text"
				label="Text body"
				required
				description="Sent alongside the HTML. Some clients show only this, and spam filters read it."
			>
				<textarea
					id="template-body-text"
					class="block w-full p-2.5 font-mono text-xs rounded-lg bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-white border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500"
					rows="8"
					bind:value={bodyText}
					disabled={saving}
					on:focus={() => (lastFocused = 'bodyText')}
				/>
			</FormField>

			<div class="flex flex-wrap items-center gap-3">
				<Button type="submit" color="primary" disabled={!canSave}>
					{#if saving}
						<i class="fa-solid fa-spinner fa-spin mr-2" />
					{/if}
					Save template
				</Button>

				<Button color="alternative" disabled={previewing} on:click={fnPreview}>
					{#if previewing}
						<i class="fa-solid fa-spinner fa-spin mr-2" />
					{:else}
						<i class="fa-solid fa-eye mr-2" />
					{/if}
					Preview
				</Button>

				{#if template.isOverridden}
					<ConfirmButton
						color="red"
						disabled={reverting}
						confirmText={`Reset “${template.key}” to the copy this build ships? The wording saved for this store is deleted, and the template follows future corrections to the shipped copy again.`}
						on:confirm={fnRevert}
					>
						{#if reverting}
							<i class="fa-solid fa-spinner fa-spin mr-2" />
						{:else}
							<i class="fa-solid fa-rotate-left mr-2" />
						{/if}
						Reset to default
					</ConfirmButton>
				{/if}

				<Button color="alternative" on:click={() => dispatch('close', {})}>Close</Button>

				{#if isDirty}
					<span class="text-xs text-default">Unsaved changes.</span>
				{/if}
			</div>

			<p class="text-xs text-default">
				Preview renders what is in the editor above against sample data. It saves nothing and sends
				nothing.
			</p>
		</form>

		{#if previewing}
			<EmptyState variant="loading" title="Rendering the preview…" />
		{:else if previewError.length > 0}
			<EmptyState variant="error" title="The preview could not be rendered." description={previewError}>
				<Button color="alternative" size="sm" on:click={fnPreview}>Try again</Button>
			</EmptyState>
		{:else if preview}
			<div class="widget bg-depth space-y-3">
				<p class="card-label">
					<i class="fa-solid fa-eye text-[11px] mr-1" />Preview of the draft above
				</p>

				<div>
					<p class="card-label mb-1">Subject</p>
					<p class="font-semibold" data-testid="preview-subject">{preview.subject}</p>
				</div>

				<div>
					<p class="card-label mb-1">HTML body</p>
					<!-- `richtext` restores the paragraph and list styling preflight strips: without it
					     the rendered template collapses into one unbroken block. -->
					<div class="richtext bg-depth widget overflow-x-auto" data-testid="preview-html">
						{@html preview.bodyHTML}
					</div>
				</div>

				<div>
					<p class="card-label mb-1">Text body</p>
					<pre
						class="widget bg-depth text-xs whitespace-pre-wrap break-words"
						data-testid="preview-text">{preview.bodyText}</pre>
				</div>

				<p class="text-xs text-default">
					Rendered with sample values ({Object.keys(preview.sampleData).length} of them), not with a
					real customer's data.
				</p>
			</div>
		{/if}
	</div>
</Panel>
